/**
 * The PURE core of `src/tauri/ipc.ts` — the envelope and nothing else.
 *
 * EXTRACTED, NOT MOVED, and that distinction is the correction §1a makes to the
 * measurement's own sketch. The sketch read `invoke.ts ← src/tauri/ipc.ts lines
 * 1–150`, which is not a lift: `ipc_invoke`'s body calls four app-side things,
 * and three of them are refused here by rules that already existed.
 *
 *   `logger.debug` / `logger.error`   `@heidi/runtime/logger` is `type:runtime`;
 *                                     `type:bridge` may not reach it (§1b)
 *   `isTauri()`                       same band, same refusal — and it is the
 *                                     316-site Wave-1·P1 burn-down file besides
 *   `translateDesktopError()` + `t()` i18next is OUT by owner ruling (§0 ruling 4)
 *   `showEventNotification()`         a toast, and the architecture's own
 *                                     sentence puts toasts in app adapters
 *
 * So the split follows the architecture's sentence literally — *"raw-command and
 * legacy-envelope adapters … router, toast and product error policy stay in
 * app/domain adapters"*. What lives here is the envelope: dynamic-import the SDK,
 * `invoke`, unwrap `{ result, error }`, throw `IpcInvokeError` on `error`, throw
 * on a missing result. No logging, no translation, no toast, no suppression
 * policy.
 *
 * `src/tauri/ipc.ts` keeps `ipc_invoke` as a thin app-side adapter over
 * `ipcInvokeRaw`, re-adding today's `isTauri()` guard, `logger` calls,
 * `translateDesktopError` + `t()` and `showEventNotification` byte-for-byte. Its
 * signature and observable behaviour do not change, and `ipc.ts:151–592` is
 * byte-untouched.
 *
 * This is cheap because `@/tauri/ipc` has no external importer that reads
 * `ipc_invoke`: every consumer resolves through an app-side module in
 * `src/tauri/**`. Nothing outside sees the split.
 *
 * NO `git mv` FOR THIS FILE, deliberately, and this is the case the repo's
 * history rule calls a partial extraction: two functions out of a 592-line
 * module cannot be renamed cleanly, so the source stays in place and the
 * provenance is written down instead.
 * History: `git log --follow -- src/tauri/ipc.ts`.
 */

/**
 * The Rust command result envelope's payload.
 *
 * THE ONE REVERSE EDGE THE PAYLOAD HAD, relocated (§3). `ipc.ts` imported this
 * as `ICPResponseData` from `./controller/recording` — the single import running
 * capability → ipc rather than the other way round. Its declaration comes here;
 * `src/tauri/controller/recording.ts` re-exports it under the old name so its
 * own consumers are unaffected.
 */
export type IpcResult = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- verbatim from the ICPResponseData declaration this replaces; narrowing it is a separate change with 100+ call sites.
  data: any;
  size: number;
};

/** The `{ result, error }` envelope every legacy Rust command replies with. */
export type IpcResponse = {
  result?: IpcResult;
  error?: { message: string; error_code?: string };
};

export type IpcEventPayload = {
  message: string;
  error_code?: string;
  /**
   * Which recorder produced the error, stamped by the Rust audio producer
   * (`utils::event::ProducerErrorPayload`).
   *
   * This is the canonical explanation on the frontend side; the handler in
   * `src/tauri/ipc.ts` points here rather than restating it.
   *
   * `AUDIO_DEVICE_LOST` has to reach one of two mutually exclusive places: the
   * online recovery, which issues server mutations, or the offline
   * notification, which must not. Classifying from
   * `offlineActiveRecordingIdAtom` alone is wrong at both edges of an offline
   * capture — the atom is written only after `offline_recording_start` resolves
   * and cleared before teardown finishes, while Rust's `err_fn` is armed across
   * both. An error at either edge was therefore handled as an online one,
   * driving change-audio-input / stop-recording against a local-only session id
   * the backend has never seen. See `CaptureOrigin` in
   * `src-tauri/src/audio/record/types.rs`.
   *
   * Present on every live capture now, online included. Absent only on a
   * desktop build older than the one that added it — so its absence still
   * means "unknown", never "online", and only then does the handler fall back
   * to the atom.
   */
  capture_origin?: string;
};

/**
 * The app's per-call error-notification policy input.
 *
 * PUBLISHED HERE BUT NOT READ HERE, and that is deliberate rather than an
 * oversight. Suppression is toast policy and toast policy is app-side, so
 * `ipcInvokeRaw` throws on every envelope error regardless. The type lives on
 * the door so the app adapter's signature is the door's shape rather than a
 * second, drifting copy of it.
 */
export type IpcInvokeOptions = {
  suppressErrorCodes?: readonly string[];
};

export class IpcInvokeError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string
  ) {
    super(message);
    this.name = 'IpcInvokeError';
  }
}

/**
 * Invoke a legacy-envelope Rust command and unwrap its result.
 *
 * Throws `IpcInvokeError` when the envelope carries an `error` — the error code
 * travels on the exception so the app adapter can apply its suppression list
 * without re-parsing anything — and a plain `Error` when the envelope carries
 * neither a result nor an error.
 *
 * The SDK arrives through `await import()` rather than a static import: this
 * package's door is reached from web bundles as well as desktop ones, and
 * `ipc_invoke` has always loaded the SDK this way.
 *
 * It does NOT guard on `isTauri()`. The app adapter owns that guard and its
 * "Not running in Tauri environment" message, because the guard's purpose is to
 * produce that specific app-facing error rather than to protect this call.
 */
export async function ipcInvokeRaw(method: string, params?: object): Promise<IpcResult> {
  const { invoke } = await import('@tauri-apps/api/core');
  const response: IpcResponse = await invoke(method, { params });

  if (response.error != null) {
    throw new IpcInvokeError(response.error.message, response.error.error_code);
  }
  if (response.result == null) {
    throw new Error('No result returned');
  }
  return response.result;
}

/**
 * Invoke a Rust command with a raw argument object (no `{ params }` envelope).
 * Used by Ambient Heidi commands that are not the legacy IpcResponse shape.
 */
export async function ipcInvokeBare<T = void>(
  method: string,
  args?: Record<string, unknown>
): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(method, args);
}

/**
 * Invoke a Rust command that replies with raw bytes (no `{ result, error }`
 * envelope). Used for PCM utterance handoff from the Ambient Heidi listener.
 */
export async function ipcInvokeBinary(
  method: string,
  args?: Record<string, unknown>
): Promise<ArrayBuffer> {
  const { invoke } = await import('@tauri-apps/api/core');
  const payload = await invoke<ArrayBuffer | Uint8Array | number[]>(method, args);
  if (payload instanceof ArrayBuffer) {
    return payload;
  }
  const bytes = payload instanceof Uint8Array ? payload : Uint8Array.from(payload);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
